//src/components/Fields/FieldRenderer.jsx
//jul23 FieldRenderer without fix for formpreview helper text disappearing:
import React, { useState, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import TextFieldPreview              from './TextFieldPreview';
import SingleSelectFieldPreview      from './SingleSelectFieldPreview';
import MultiSelectFieldPreview       from './MultiSelectFieldPreview';
import MultiLineTextPreview          from './MultiLineTextPreview';
import TextFieldComponent            from './TextFieldComponent';
import ReadOnlyTextFieldComponent    from './ReadOnlyTextFieldComponent';
import MultiLineTextFieldComponent   from './MultiLineTextFieldComponent';
import SingleSelectFieldComponent    from './SingleSelectFieldComponent';
import MultiSelectFieldComponent     from './MultiSelectFieldComponent';
import URLFieldPreview               from './URLFieldPreview';
import URLFieldComponent             from './URLFieldComponent';
import DateFieldComponent           from './DateFieldComponent';

import DatePickerFieldPreview        from './DatePickerFieldPreview';
import HeaderFieldComponent          from './HeaderFieldComponent';
import HeaderPreview                 from './HeaderPreview';
import ImagePreview                  from './ImagePreview';
import ImageFieldComponent           from './ImageFieldComponent';
import AttachFilePreview             from './AttachFilePreview';

import HoveredField                  from '../FormSettings/FormDesignLayout/HoveredField';
import { useFormLayout }             from '../../contexts/FormLayoutContext';
import { useFieldEdit }              from '../../contexts/FieldEditContext';
import { useFormTheme }              from '../../contexts/FormThemeContext';
import SubmitButtonComponent         from '../FormSettings/FormDesignLayout/SubmitButtonComponent';
import { useFieldMetadata }          from '../../contexts/FieldMetadataContext';
import { useMultiAnswerField }       from '../../contexts/MultiAnswerFieldContext';
import { useDropdownDependencies }   from '../../contexts/DropdownDependenciesContext';
import { useFormAnswers }            from '../../contexts/FormAnswersContext';
import { useAssetMetadata }          from '../../contexts/AssetMetadataContext';

const debugMode = process.env.NODE_ENV === 'development';

/* -------------------------------------------------------------------------- */
/*  Lazy field chunks                                                         */
/* -------------------------------------------------------------------------- */
/*
 * ENTERPRISE:
 * AttachFieldComponent pulls in DAM/AUF-heavy runtime code such as AssetLibrary,
 * VersionsDrawer, asset previews, AUF handoff helpers, and several asset/form
 * contexts. Keep that code out of simple public-form embeds unless the form
 * actually contains an attach field.
 *
 * This does NOT change attach-field behavior. It only defers downloading the
 * attach runtime until FieldRenderer encounters type === 'attach'.
 */
const LazyAttachFieldComponent = lazy(
  /* webpackChunkName: "field-attach" */ () => import('./AttachFieldComponent')
);

/* -------------------------------------------------------------------------- */
/*                         Component definition / props                       */
/* -------------------------------------------------------------------------- */
const FieldRenderer = (props) => {
  // Destructure inside the function to avoid parser ambiguity with comments
  const {
    id,
    type,
    label,
    required,
    index,
    mode,
    propValue,
    onChange = () => {},

    // -------- Props forwarded from DraggableItem --------------------------
    // 🔹 NEW – may arrive from parent
    fieldMetadata: fieldMetadataProp,
    // already present in parent
    showDropZones,
    // 🔹 NEW – from parent
    setShowDropZones,
    // already defaulted
    isDragging = false,
    // NEW: read-only enforcement from view surface
    viewOnly = false,
  } = props;

  /* Avoid clobbering the setShowDropZones prop by renaming ctx value */
  const {
    setShowDropZones: setShowDropZonesFromCtx,
    layoutConfig,
  } = useFormLayout(); 

  const {
    selectedFieldForEdit,
    fieldChanges = {},
    updateField,
    isFieldChangesReady,
    validationErrors,
    handleFieldChange,
  } = useFieldEdit();

  const { fieldVariant, colors, fonts } = useFormTheme();
  const { fieldsMetadata } = useFieldMetadata();
  const { dropdownOptions } = useMultiAnswerField();
  const {
    dynamicDropdownOptions,   // filtered options kept by the engine
    notifyDependentFields,    // broadcast when a driver changes
    loadDependenciesForField, // lazy load per optionsSetUFI
    loadingMap,               // per-UFI loading flags (optional helper)
    getFilteredOptions,       // keep if you still use it elsewhere
  } = useDropdownDependencies();


  /* Use prop override if provided, else fallback to global metadata */
  const fieldMetadata =
    fieldMetadataProp ||                     
    fieldsMetadata[id];

  // ─────────────────────────────────────────────────────────────
  // FormAnswersContext integration
  // - UFI is the canonical key for answers (fallback to fieldId)
  // - get/set answer APIs keep values canonical (ids for dropdowns)
  // ─────────────────────────────────────────────────────────────
  const ufi = fieldMetadata?.ufi || id;

  const optionsSetUFI = fieldMetadata?.optionsSetUFI || null;

  useEffect(() => {
    if (!optionsSetUFI || typeof loadDependenciesForField !== 'function') return;

    // Kick off a fetch only once per UFI (undefined means “not started”)
    if (loadingMap?.[optionsSetUFI] === undefined) {
      loadDependenciesForField(optionsSetUFI);
    }
  }, [optionsSetUFI, loadDependenciesForField, loadingMap]);

  // Answers API (guard if provider not mounted for safety)
const {
  getAnswer,
  setAnswer,
  getAnswersSnapshot,   // may be undefined in your impl
  answersByUfi          // live map of answers (UFI → value)
} = useFormAnswers() || {};

const { getAssetRuntime } = useAssetMetadata() || {};

// Lightweight “change token” so memos re-run when any answer changes.
// If the provider doesn’t expose getAnswersSnapshot yet, this will be null (safe).
const answersSnapshot = useMemo(
  () => (typeof getAnswersSnapshot === 'function' ? getAnswersSnapshot() : null),
  [getAnswersSnapshot]
);

// Focus tracking for onBlur-driven text fields (prevents answer→local overwrite while typing)
const [isEditing, setIsEditing] = useState(false);
const handleFocus = useCallback(() => setIsEditing(true), []);


// 🔎 DIAG: whenever answers change, show the inputs to the disable rule
useEffect(() => {
  const depUfi = fieldMetadata?.disabledDependentFieldId;
  if (!depUfi) return;

  // live answer preferred
  const depAnswer =
    (typeof getAnswer === 'function' ? getAnswer(depUfi) : undefined);

  // fallback default (metadata is keyed by fieldId → resolve by UFI)
  const depMeta = Object.values(fieldsMetadata || {}).find(m => m?.ufi === depUfi);
  const depDefault = depMeta?.defaultValue;

//  console.log('[FieldRenderer:disable-check]', {
//    id,
//    type,
//    mode,
//    depUfi,
//    depAnswer,
//    depDefault
//  });
}, [
  // re-run when the answers map changes
  answersByUfi,

  fieldMetadata?.disabledDependentFieldId,
  getAnswer,
  fieldsMetadata,
  id,
  type,
  mode
]);


// Helper: normalize attach values (object | array | null)
const normalizeAttachValue = (v) => {
  if (v == null) return null;           // null/undefined → null
  if (Array.isArray(v)) return v;       // arrays OK
  if (typeof v === 'object') return v;  // objects OK
  return null;                          // anything else (incl. '') → null
};

/* ---------------------------------------------------------------------- */
/*                 Local state for real-time value updates                */
/* ---------------------------------------------------------------------- */
// Preserve '' for non-attach fields; use null only for attach
const pickDefault = (a, b, fallback) =>
  (a !== undefined ? a : (b !== undefined ? b : fallback));

// Prefer current answer (canonical) → then pending fieldChanges → then metadata default
const answerValue = typeof getAnswer === 'function' ? getAnswer(ufi) : undefined;
const initialValueRaw =
  (answerValue !== undefined) ? answerValue :
  (fieldChanges[id]?.defaultValue !== undefined) ? fieldChanges[id].defaultValue :
  (fieldMetadata?.defaultValue !== undefined) ? fieldMetadata.defaultValue :
  (type === 'attach' ? null : '');

const initialValue =
  (type === 'attach')
    ? normalizeAttachValue(initialValueRaw)
    : initialValueRaw;

const [localValue, setLocalValue] = useState(initialValue);


// Keep local state in sync only when the parent actually supplies a value.
// This avoids wiping localValue (e.g., attach’s { assetId, version }) when
// propValue is undefined / not controlled at the parent.
useEffect(() => {
  if (typeof propValue === 'undefined') return;

  const next =
    (type === 'attach')
      ? normalizeAttachValue(propValue)
      : propValue;

  // ────────────────────────────────────────────────────────────────
  // AUF Flicker Diagnostics: propValue clobber check (LOG ONLY)
  // Enable:
  //   window.__DEBUG_AUF_FLICKER__ = true
  // ────────────────────────────────────────────────────────────────
  try {
    if (typeof window !== 'undefined' && window.__DEBUG_AUF_FLICKER__ === true) {
//      console.log('[AUF:Flicker][FieldRenderer] propValue sync', {
//        id,
//        type,
//        mode,
//        ufi,
//        propValueType: propValue == null ? 'null' : Array.isArray(propValue) ? 'array' : typeof propValue,
//        propValue,
//        normalizedNext: next,
//        localValueBefore: localValue,
//      });
    }
  } catch (_) {}

  if (next !== localValue) setLocalValue(next);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [propValue, id, type]);

// Keep local state in sync if the canonical answer changes externally
useEffect(() => {
  if (typeof getAnswer !== 'function') return;
  const nextAnswer = getAnswer(ufi);
  const normalized =
    (type === 'attach')
      ? normalizeAttachValue(nextAnswer)
      : nextAnswer;

  // Do not pull from canonical while the user is actively editing this field
  if (!isEditing && normalized !== undefined && normalized !== localValue) {
    setLocalValue(normalized);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [getAnswer, ufi, type, isEditing]);


  /* ---------------------------------------------------------------------- */
  /*                      Change / blur handlers (unchanged)                */
  /* ---------------------------------------------------------------------- */
const handleLocalValueChange = useCallback((event) => {
  let newValue;
  if (typeof event === 'object' && event?.optionId) {
    // (Rare) direct option object — wrapper *should* already normalize,
    // but keep this just in case
    newValue = event.optionId;
  } else if (typeof event === 'object' && event?.target && ('value' in event.target)) {
    // Normalized from CustomFieldWrapper:
    //  - text → string
    //  - single-select → optionId
    //  - multi-select → optionId[]
    //  - date → formatted string
    //  - attach → object
    newValue = event.target.value;
  } else if (typeof event !== 'undefined') {
    newValue = event;
  } else {
    console.error('[FieldRenderer] Unexpected event object:', event);
    return;
  }

  setLocalValue(newValue);

  // Keep legacy callback behavior (some parents rely on it)
  onChange(newValue);

  // Write to FormAnswersContext (canonical)
 if (typeof setAnswer === 'function') {
   try {
     const isDropdown  = (type === 'single-select' || type === 'multi-select');
     const isImmediate  = isDropdown || type === 'date-picker' || type === 'attach' || type === 'urlField';
     if (isImmediate) {
       // Immediate updates for non-text inputs (low frequency)
       setAnswer(ufi, newValue);
     }
     // For text-like fields, we do NOT write here—blur will persist.
   } catch (e) {
     console.warn('[FieldRenderer] setAnswer failed:', e);
   }
 }


  // Only patch field metadata (defaultValue) in the BUILDER
  if (mode === 'builder') {
    updateField(id, { defaultValue: newValue });
  }
}, [onChange, id, mode, setAnswer, ufi, updateField, type]);


const handleBlur = useCallback(() => {
  // Always ensure the canonical answer is up-to-date
  if (typeof setAnswer === 'function') {
    try { setAnswer(ufi, localValue); } catch (_) {}
  }

  // Only persist "defaultValue" back to metadata in the BUILDER
  if (mode === 'builder') {
    const prevRaw =
      (fieldChanges[id]?.defaultValue !== undefined)
        ? fieldChanges[id].defaultValue
        : ((fieldMetadata?.defaultValue !== undefined) ? fieldMetadata.defaultValue : '');

    if (prevRaw !== localValue) {
      updateField(id, { defaultValue: localValue });
      handleFieldChange(id, { defaultValue: localValue });
    }
  }

  // We’re no longer editing; allow answer→local sync again
  setIsEditing(false);
}, [mode, id, ufi, localValue, setAnswer, fieldChanges, fieldMetadata, updateField, handleFieldChange]);


  /* ---------------------------------------------------------------------- */
  /*                   Validation / styling helpers (unchanged)             */
  /* ---------------------------------------------------------------------- */
  const isFieldInvalid = useMemo(
    () => !!validationErrors?.[id],
    [validationErrors, id],
  );

/* ------------------------------------------------------------------ */
/*  helperText – adds LOG‑4a for preview debugging                    */
/* ------------------------------------------------------------------ */
const helperText = useMemo(() => {
  /* ─── LOG‑4a ───────────────────────────────────────────────────── */
  const chosen =
    validationErrors?.[id]?.errorMessage
      ? validationErrors[id].externalHelperText ||
        validationErrors[id].errorMessage
      : (isFieldInvalid
          ? fieldMetadata?.externalHelperText || 'This field is required.'
          : fieldMetadata?.helperText);

//  console.log('[FieldRenderer:LOG‑4a] helperText decision', {
//    id,
//    metaHelper: fieldMetadata?.helperText,
//    validationErr: validationErrors?.[id]?.errorMessage,
//    external: validationErrors?.[id]?.externalHelperText,
//    chosen,
//  });
  /* ─────────────────────────────────────────────────────────────── */

  return chosen;                    // ⬅ explicit return is required
}, [validationErrors, id, isFieldInvalid, fieldMetadata]);
/* ------------------------------------------------------------------ */

  const fieldStyle = useMemo(() => ({
    ...fieldMetadata?.style,
    borderColor: isFieldInvalid ? colors.warningColor : fieldMetadata?.style?.borderColor,
    color: colors.textColor,
    fontFamily: fonts.primaryFont,
    backgroundColor: colors.backgroundColor,
  }), [fieldMetadata?.style, isFieldInvalid, colors, fonts]);

// ─────────────────────────────────────────────────────────────
// Helper: find a field's defaultValue by UFI (fieldsMetadata is keyed by fieldId)
// ─────────────────────────────────────────────────────────────
const getDefaultByUfi = useCallback((depUfi) => {
  if (!depUfi || !fieldsMetadata) return undefined;
  const meta = Object.values(fieldsMetadata || {}).find(m => m?.ufi === depUfi);
  return meta?.defaultValue;
}, [fieldsMetadata]);

//new code
 // ─────────────────────────────────────────────────────────────
 // Selector: only the controlling field’s current answer token
 // (prevents isDisabled from recomputing for unrelated fields)
 // ─────────────────────────────────────────────────────────────
 const dependentFieldUFI = fieldMetadata?.disabledDependentFieldId || null;
 // Pull just the single driver’s value out of the map so our deps can be narrow
 const depAnswerFromMapForDeps =
   dependentFieldUFI && answersByUfi
     ? answersByUfi[dependentFieldUFI]
     : undefined;

/* ---------------------------------------------------------------------- */
/*                  Disable logic (final + builder aligned)               */
/* ---------------------------------------------------------------------- */
const isDisabled = useMemo(() => {
  if (mode === 'builder') return false;

 const dependentFieldUFI = fieldMetadata?.disabledDependentFieldId;

  if (dependentFieldUFI) {
    // Prefer live map → fallback function → fallback default
   const depAnswerFromMap = depAnswerFromMapForDeps;


    const depAnswerFromFn =
      (depAnswerFromMap === undefined && typeof getAnswer === 'function')
        ? getAnswer(dependentFieldUFI)
        : undefined;

    const dependentVal =
      depAnswerFromMap !== undefined ? depAnswerFromMap
      : depAnswerFromFn !== undefined ? depAnswerFromFn
      : getDefaultByUfi(dependentFieldUFI);

    const disabledBecauseBlank =
      (dependentVal == null) ||
      (typeof dependentVal === 'string' && dependentVal.trim() === '') ||
      (Array.isArray(dependentVal) && dependentVal.length === 0);

    const computed = disabledBecauseBlank
      ? true
      : (fieldChanges?.[id]?.disabled ?? fieldMetadata?.disabled ?? false);

//    console.log('[FieldRenderer:isDisabled]', {
//      id,
//      type,
//      mode,
//      depUfi: dependentFieldUFI,
//      dependentVal,
//     disabledComputed: computed
//    });

    return computed;
  }

  const computed = (fieldChanges?.[id]?.disabled ?? fieldMetadata?.disabled ?? false);

//  console.log('[FieldRenderer:isDisabled]', {
//    id,
//    type,
//    mode,
//    depUfi: null,
//    dependentVal: undefined,
//    disabledComputed: computed
//  });

  return computed;
}, [
  mode,
  id,
  type,
  fieldMetadata,
  fieldChanges,
 // only re-run when the controlling field’s value token changes
 depAnswerFromMapForDeps,

  // still keep these helpers
  getAnswer,
  getDefaultByUfi,
  fieldsMetadata
]);



  /* ---------------------------------------------------------------------- */
  /*                   Assemble base props for field component              */
  /* ---------------------------------------------------------------------- */
  const fieldProps = useMemo(() => {
    let fieldPropsBase = {
      fieldId: id,
      fieldMetadata,
      placeholder: fieldMetadata?.placeholder || 'Enter some text',
      value: localValue,
      onChange: handleLocalValueChange,
      onBlur: handleBlur,
      onFocus: handleFocus,
      /* ENFORCE non-editable in final view mode */
      disabled: (mode === 'final' && viewOnly) || (mode === 'builder') || isDisabled,
      readOnly: (mode === 'final' && viewOnly) ? true : undefined,
      isBuilderMode: mode !== 'final',

      /* Pass whichever setShowDropZones is available */
      setShowDropZones: setShowDropZones || setShowDropZonesFromCtx,    /* 🔹 UPDATED */
      showDropZones,                                                    /* 🔹 NEW */

      style: fieldStyle,
      variant: fieldVariant,
      error: !!validationErrors?.[id]?.errorMessage || isFieldInvalid,
      helperText,
      externalHelperText: validationErrors?.[id]?.externalHelperText || fieldMetadata?.externalHelperText,
      label: (fieldMetadata?.label ?? label),
      multiple: type === 'multi-select',
      required: fieldMetadata.required,
      maxLength: fieldMetadata.maxLength,
      hidden: fieldMetadata.hidden,
      transformations: fieldMetadata.transformations || {},
    };//    console.log(`[FieldRenderer] Field ${id} transformations in FormPreview:`, fieldPropsBase.transformations);

    if (type === 'header') {
//        console.log(`[FieldRenderer] Metadata for header field ${id} - Expected textAlign:`, fieldMetadata?.style?.textAlign);

        // Step 3: Add log to confirm textAlign appears in fieldProps
//        console.log(`[FieldRenderer] Final fieldProps for header field ${id}:`, fieldPropsBase);
    }

 const canonicalValue = (answerValue !== undefined) ? answerValue : localValue;

 // ─────────────────────────────────────────────────────────────
 // System read-only fields (enterprise)
 // - Render-only: compute a value from other answers (ex: attach.versionNumber)
 // - Do NOT write to FormAnswersContext here (no setAnswer side-effects)
 // - This keeps your existing submit pipeline + avoids loops.
 // ─────────────────────────────────────────────────────────────
 const isReadOnlyText =
   type === 'read-only-text' ||
   fieldMetadata?.type === 'read-only-text' ||
   fieldMetadata?.readOnly === true ||
   fieldMetadata?.isReadOnly === true;

 const systemKey = fieldMetadata?.systemKey || fieldMetadata?.semanticKey || null;

 // Helper: find the first attach OR image field in THIS layout (AUF pattern)
 const resolvePrimaryAttachUfi = () => {
   try {
     const all = Object.values(layoutConfig || {}).flat();
     for (const f of all) {
       const meta = fieldsMetadata?.[f.id];
       const t = meta?.type || f.type;
       if (t === 'attach' || t === 'image') return meta?.ufi || f.id;
     }
   } catch (_) {}
   return null;
 };

 // Compute runtime/system value from answers (read-only)
 // For v1: support "asset.versionNumber" from the attach/image field value:
 // - attach/image answer is { assetId, versionNumber } OR null
 // - for AUF pending file: { kind:'file', ... } -> no version yet
 let computedSystemValue = undefined;

 if (isReadOnlyText && systemKey) {
   if (systemKey === 'asset.versionNumber' || systemKey === 'asset.version') {
     const attachUfi =
       fieldMetadata?.sourceUfi ||                // optional explicit binding
       resolvePrimaryAttachUfi();                 // fallback: first attach/image in layout

     // ✅ ENTERPRISE: prefer live map (answersByUfi) so we re-render when hydrator sets answers
     const attachVal =
       attachUfi && answersByUfi
         ? answersByUfi[attachUfi]
         : (
             attachUfi && typeof getAnswer === 'function'
               ? getAnswer(attachUfi)
               : undefined
           );

     // Attach value can be:
     // - library/uploaded ref: { assetId, versionNumber? }
     // - AUF pending: { kind:'file', ... } (no assetId yet)
     const attachAssetId =
       (attachVal && typeof attachVal === 'object')
         ? (attachVal.assetId || attachVal.id || null)
         : null;

     // ENTERPRISE: prefer runtime cache (hydrated by AttachFieldComponent / preview meta)
     const runtime =
       (attachAssetId && typeof getAssetRuntime === 'function')
         ? getAssetRuntime(attachAssetId)
         : null;

     const vNum =
       (runtime && typeof runtime.versionNumber === 'number')
         ? runtime.versionNumber
         : (
             (attachVal && typeof attachVal === 'object' && typeof attachVal.versionNumber === 'number')
               ? attachVal.versionNumber
               : null
           );

     // Legacy string fallback (only if you still have it in some places)
     const legacy =
       (attachVal && typeof attachVal === 'object' && typeof attachVal.version === 'string' && attachVal.version.length > 0)
         ? attachVal.version
         : null;

     // ✅ IMPORTANT: return JUST the number for your Version # system field (no "v" prefix)
     computedSystemValue =
       vNum != null ? String(vNum) :
       (legacy ? String(legacy) : '');

     // (optional) debug trace — keep commented unless needed
     // console.log('[FieldRenderer][system:versionNumber]', {
     //   fieldId: id,
     //   systemKey,
     //   attachUfi,
     //   attachAssetId,
     //   runtimeVersion: runtime?.versionNumber,
     //   attachVersion: attachVal?.versionNumber,
     //   computedSystemValue
     // });
   }
 }

 // If this is a read-only system field, override the rendered value only.
 // IMPORTANT: we do NOT mutate answers here.
 if (isReadOnlyText) {
   fieldPropsBase = {
     ...fieldPropsBase,
     value: (computedSystemValue !== undefined) ? computedSystemValue : '',
     // For system fields, let it look enabled-but-readonly (component handles readOnly UX)
     disabled: (mode === 'final' && viewOnly) || (mode === 'builder') || false,
   };
 }


// Track whether this field has a system-computed value that must not be overwritten
const hasSystemOverrideValue =
  isReadOnlyText &&
  systemKey &&
  (computedSystemValue !== undefined);

// Dropdown-specific logic
if (type === 'single-select' || type === 'multi-select') {
  const setId = fieldMetadata?.optionsSetUFI;

  // Use the same canonical value when deriving filtering key
  const currentSelection = canonicalValue;

  const baseList =
    (dynamicDropdownOptions?.[setId]) ??
    (dropdownOptions?.[setId]) ??
    [];

  let filteredList = Array.isArray(baseList) ? baseList : [];

  const selectedId =
    Array.isArray(currentSelection) ? currentSelection[0] : currentSelection;

  if (selectedId) {
    const hasSelected =
      Array.isArray(filteredList) &&
      filteredList.some(o => o?.optionId === selectedId);

    if (!hasSelected) {
      const fullSet = Array.isArray(dropdownOptions?.[setId])
        ? dropdownOptions[setId]
        : [];

      const selectedObj = fullSet.find(o => o?.optionId === selectedId);
      if (selectedObj) {
        filteredList = [...filteredList, selectedObj];
      }
    }
  }

  const optionsWithHints = filteredList.map(option => ({
    label   : option?.label ?? '',
    optionId: option?.optionId ?? '',
    hint    : option?.hint || '',
  }));

  fieldPropsBase = {
    ...fieldPropsBase,
    value        : canonicalValue,    // dropdowns remain canonical
    options      : optionsWithHints,
    optionsSetUFI: setId,
    disableClearable: (mode === 'final' && viewOnly) ? true : undefined,
  };

} else {
  // Non-dropdown:
  // IMPORTANT: do NOT overwrite system-computed read-only values
  if (!hasSystemOverrideValue) {
    fieldPropsBase = {
      ...fieldPropsBase,
      value: canonicalValue,
    };
  }
}


// Simplified date-picker handling
if (type === 'date-picker') {
  const dateValue =
    (answerValue !== undefined) ? answerValue :
    (fieldChanges[id]?.defaultValue !== undefined) ? fieldChanges[id].defaultValue :
    (fieldMetadata.defaultValue || '');

    fieldPropsBase = {
        ...fieldPropsBase,
        dateFormat: fieldMetadata.dateFormat || 'MM/DD/YYYY', // Default date format
        dateMode: fieldMetadata.dateMode || 'single', // Default to single date mode
        placeholder: fieldMetadata.placeholder || 'MM/DD/YYYY', // Placeholder text
        value: dateValue, // Pass raw defaultValue directly
    };

    // Log the final value passed to DatePickerFieldComponent
    //console.log(`[FieldRenderer] Passing raw date value to DatePickerFieldComponent: "${fieldPropsBase.value}"`);
}

//  console.log(`[FieldRenderer: debug] Passing error state to component for field ${id}:`, fieldPropsBase.error, "Validation Errors:", validationErrors?.[id]);

    // 🔎 DIAG: final props (esp. disabled + value)
//    console.log('[FieldRenderer:props]', {
//      id,
//      type,
//      mode,
//      value: fieldPropsBase.value,
//      disabled: fieldPropsBase.disabled,
//      depUfi: fieldMetadata?.disabledDependentFieldId
//    });

    return fieldPropsBase;
}, [
  // identities / inputs
  id,
  type,
  label,
  mode,
  index,
  viewOnly,

  // metadata + layout + options
  fieldMetadata,
  fieldsMetadata,
  layoutConfig,
  dropdownOptions,
  dynamicDropdownOptions,

  // answers (this is the big one)
  answerValue,
  answersByUfi,
  answersSnapshot,
  getAnswer,
  setAnswer,

  // handlers + local state
  localValue,
  isEditing,
  handleLocalValueChange,
  handleBlur,
  handleFocus,

  // styling + validation + helper
  fieldStyle,
  fieldVariant,
  validationErrors,
  isFieldInvalid,
  helperText,
  getFilteredOptions,
  fieldChanges,

  // dropzone props
  setShowDropZones,
  setShowDropZonesFromCtx,
  showDropZones,

  // system/runtime cache
  getAssetRuntime,

  // dependency engine helpers used by isDisabled
  depAnswerFromMapForDeps,
  getDefaultByUfi,
]);
 /* ---------------------------------------------------------------------- */
  /*                     Early guards and mode rendering                    */
  /* ---------------------------------------------------------------------- */
  if (!isFieldChangesReady) return <div>Loading...</div>;
  if (!id || !fieldMetadata || !fieldMetadata.type) {
    console.error('[FieldRenderer] Invalid fieldId or missing metadata:', id);
    return null;
  }

  const wrapInHovered = (Component) => (
    <HoveredField fieldId={id} isDragging={isDragging}>
      <Component {...fieldProps} />
    </HoveredField>
  );

const renderLazyAttachField = () => (
  <Suspense fallback={null}>
    <LazyAttachFieldComponent {...fieldProps} />
  </Suspense>
);

  const wrapLazyAttachInHovered = () => (
    <HoveredField fieldId={id} isDragging={isDragging}>
      {renderLazyAttachField()}
    </HoveredField>
  );

  const renderField = () => {
    switch (mode) {
      case 'preview':
        if (type === 'attach') {
          return <AttachFilePreview id={id} index={index} {...fieldProps} />;
        }
        if (type === 'single-select') {
          return <SingleSelectFieldPreview {...fieldProps} />;
        }
        if (type === 'multi-select') {
          return <MultiSelectFieldPreview {...fieldProps} />;
        }
        if (type === 'urlField') {
          return <URLFieldPreview {...fieldProps} />;
        }
        if (type === 'multi-line-text') {
          return <MultiLineTextPreview {...fieldProps} />; // Render preview for multi-line
        }
        if (type === 'date-picker') {
          return <DatePickerFieldPreview id={id} index={index} {...fieldProps} />; // Use DatePickerFieldPreview for date-picker in preview mode
        }
        if (type === 'image') {
          return <ImagePreview id={id} index={index} {...fieldProps} />;
        }
        if (type === 'header') {
          return <HeaderPreview id={id} index={index} {...fieldProps} />;
        }        
        if (type === 'read-only-text') {
          return <TextFieldPreview {...fieldProps} />;
        }
        return <TextFieldPreview {...fieldProps} />;
     case 'builder':
        if (type === 'submit') return <SubmitButtonComponent {...fieldProps} />;
  if (type === 'attach') {
    // TEMP TRACE — remove after verifying
//    console.log('[FieldRenderer] attach/builder', {
//      id,
//      mode,
//      valueType: typeof fieldProps.value,
//      value: fieldProps.value,
//    });
    return wrapLazyAttachInHovered();
  }

        if (type === 'single-select')   return wrapInHovered(SingleSelectFieldComponent);
        if (type === 'multi-select')    return wrapInHovered(MultiSelectFieldComponent);
        if (type === 'urlField')        return wrapInHovered(URLFieldComponent);
        if (type === 'multi-line-text') return wrapInHovered(MultiLineTextFieldComponent);
        if (type === 'date-picker')     return wrapInHovered(DateFieldComponent);
        if (type === 'image')           return wrapInHovered(ImageFieldComponent);
        if (type === 'header')          return wrapInHovered(HeaderFieldComponent);
        if (type === 'read-only-text')  return wrapInHovered(ReadOnlyTextFieldComponent);
        return wrapInHovered(TextFieldComponent);
      case 'final':
        if (type === 'submit') {
          return <SubmitButtonComponent {...fieldProps} />;
        }
        if (type === 'attach') {
          return renderLazyAttachField();
        }
        if (type === 'single-select') {
          return <SingleSelectFieldComponent {...fieldProps} />;
        }
        if (type === 'multi-select') {
          return <MultiSelectFieldComponent {...fieldProps} />;
        }
        if (type === 'urlField') {
          return <URLFieldComponent {...fieldProps} />;
        }
        if (type === 'multi-line-text') {
          return <MultiLineTextFieldComponent {...fieldProps} />; // Render multi-line component in final mode
        }
        if (type === 'image') {
          return <ImageFieldComponent {...fieldProps} />; 
        }
        if (type === 'header') {
          return <HeaderFieldComponent {...fieldProps} />; 
        }
         if (type === 'date-picker') {
           return <DateFieldComponent {...fieldProps} />;
         } 
         if (type === 'read-only-text') {
           return <ReadOnlyTextFieldComponent {...fieldProps} />;
         }
        return <TextFieldComponent {...fieldProps} />;
      default:
        console.error("[FieldRenderer - renderField] Unsupported mode:", mode);
        return null;
    }
  };

  return renderField();
};

/* -------------------------------------------------------------------------- */
/*                               Prop-types                                   */
/* -------------------------------------------------------------------------- */
FieldRenderer.propTypes = {
  id:           PropTypes.string.isRequired,
  type:         PropTypes.string.isRequired,
  index:        PropTypes.number.isRequired,
  mode:         PropTypes.oneOf(['preview', 'builder', 'final']).isRequired,
  onChange:     PropTypes.func,
  showDropZones:    PropTypes.bool, 
  setShowDropZones: PropTypes.func, 
  isDragging:   PropTypes.bool,
  fieldMetadata: PropTypes.object,   
  viewOnly:    PropTypes.bool,        
};
export default React.memo(FieldRenderer);
